import { PropsWithChildren, ReactNode, useContext, useRef } from 'react';

import { CopyButton } from '@components/patterns/CopyButton';
import { DataListContext } from '@components/patterns/DataList/context';
import { DataListItemVariant } from '@components/patterns/DataList/types';
import { cn, sentenceCase } from '@components/primitives/utilities';
import { toast } from '@vault';

import css from './styles.module.css';

export interface DataListItemProps extends PropsWithChildren {
  className?: string;
  variant?: DataListItemVariant;
  icon?: ReactNode;
  label: string;
  /**
   * - Pass `true` to enable copy functionality.
   * - Pass a string to use as the value to copy.
   * - Pass a function to dynamically generate the value to copy.
   */
  copy?: string | (() => string) | boolean;
  suffix?: ReactNode;
}

export function DataListItem({
  className,
  label,
  suffix,
  copy,
  children,
  icon,
  variant = 'default',
}: DataListItemProps) {
  const context = useContext(DataListContext);
  const fullLabel = [...context.groups, label].join(' ');

  const valueRef = useRef<HTMLSpanElement>(null);
  async function getContent() {
    if (typeof copy === 'string') {
      return copy;
    } else if (typeof copy === 'function') {
      return copy();
    } else {
      return valueRef.current?.textContent ?? '';
    }
  }

  return (
    <div className={cn(css.item, className)}>
      <dt className={css.label}>
        <span className={css.labelText}>{label}</span>
      </dt>
      <dd className={css.value}>
        {icon && <span className={css.valueIcon}>{icon}</span>}

        <span className={css.valueText}>
          <span
            ref={valueRef}
            className={css.valueTextContent}
            data-variant={variant}
          >
            {children}
          </span>
        </span>

        {(suffix || copy) && (
          <span className={css.valueSuffix}>
            {suffix}

            {copy && (
              <CopyButton
                className={css.suffixButton}
                size="2xs"
                ghost
                flush
                value={getContent}
                title={sentenceCase(`Copy ${fullLabel}`)}
                onCopy={() =>
                  toast(sentenceCase(`Copied ${fullLabel}`), {
                    id: `copy-${fullLabel}`,
                  })
                }
                tooltip={false}
              />
            )}
          </span>
        )}
      </dd>
    </div>
  );
}
